import type { UsersAndTeams } from '@momentum/types';
import { Lock } from 'lucide-react';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/shared/components/ui/Command';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/Tooltip';
import {
  usePeopleTeamData,
  type PeopleTeamItem,
} from '@/shared/components/people-team-list/PeopleTeamList';
import {
  PersonAvatar,
  TeamIcon,
  SelectedCheckmark,
} from '@/shared/components/people-team-list/PeopleTeamAvatars';

interface UserTeamPickerPopoverProps {
  value: UsersAndTeams;
  onChange: (value: UsersAndTeams) => void;
  searchPlaceholder?: string;
  teamsLabel?: string;
  peopleLabel?: string;
  noResultsLabel?: string;
  lockedIds?: { users?: string[]; teams?: string[] };
  lockedTooltip?: string;
  onEscape?: () => void;
}

export function UserTeamPickerPopover({
  value,
  onChange,
  searchPlaceholder = 'Search...',
  teamsLabel = 'Teams',
  peopleLabel = 'People',
  noResultsLabel = 'No results found',
  lockedIds,
  lockedTooltip,
  onEscape,
}: UserTeamPickerPopoverProps) {
  const { teamItems, personItems } = usePeopleTeamData();

  const isLocked = (item: PeopleTeamItem): boolean => {
    if (!lockedIds) return false;
    return item.type === 'team'
      ? lockedIds.teams?.includes(item.id) ?? false
      : lockedIds.users?.includes(item.id) ?? false;
  };

  const toggleItem = (item: PeopleTeamItem) => {
    if (isLocked(item)) return;

    if (item.type === 'team') {
      const isSelected = value.teams?.includes(item.id);
      onChange({
        ...value,
        teams: isSelected
          ? (value.teams ?? []).filter((teamId) => teamId !== item.id)
          : [...(value.teams ?? []), item.id],
      });
    } else {
      const isSelected = value.users?.includes(item.id);
      onChange({
        ...value,
        users: isSelected
          ? (value.users ?? []).filter((userId) => userId !== item.id)
          : [...(value.users ?? []), item.id],
      });
    }
  };

  return (
    <Command
      className="bg-popover"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onEscape?.();
        }
      }}
    >
      <CommandInput placeholder={searchPlaceholder} className="text-[13px]" />
      <CommandList className="max-h-[200px]">
        <CommandEmpty className="px-2 py-3 text-[13px] text-muted-foreground text-center">
          {noResultsLabel}
        </CommandEmpty>

        {teamItems.length > 0 && (
          <CommandGroup
            heading={teamsLabel}
            className="[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.04em] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1"
          >
            {teamItems.map((item) => {
              const locked = isLocked(item);
              const selected = value.teams?.includes(item.id);
              return (
                <CommandItem
                  key={`team-${item.id}`}
                  value={`team-${item.name}`}
                  onSelect={() => toggleItem(item)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-[13px] text-foreground text-start ${
                    locked ? 'opacity-60 cursor-default' : 'cursor-pointer'
                  }`}
                >
                  <TeamIcon />
                  {item.name}
                  {locked && selected ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="ms-auto flex shrink-0">
                            <Lock className="size-3.5 text-muted-foreground" />
                          </span>
                        </TooltipTrigger>
                        {lockedTooltip && (
                          <TooltipContent>{lockedTooltip}</TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    selected && <SelectedCheckmark />
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {personItems.length > 0 && (
          <CommandGroup
            heading={peopleLabel}
            className="[&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.04em] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1"
          >
            {personItems.map((item) => {
              const locked = isLocked(item);
              const selected = value.users?.includes(item.id);
              return (
                <CommandItem
                  key={`user-${item.id}`}
                  value={`user-${item.name}`}
                  onSelect={() => toggleItem(item)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded text-[13px] text-foreground text-start ${
                    locked ? 'opacity-60 cursor-default' : 'cursor-pointer'
                  }`}
                >
                  <PersonAvatar
                    initials={item.initials}
                    imageUrl={item.imageUrl}
                  />
                  {item.name}
                  {locked && selected ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="ms-auto flex shrink-0">
                            <Lock className="size-3.5 text-muted-foreground" />
                          </span>
                        </TooltipTrigger>
                        {lockedTooltip && (
                          <TooltipContent>{lockedTooltip}</TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    selected && <SelectedCheckmark />
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}
